import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Centralized Loading component for consistent loading states across the app.
 *
 * @param {string} message - The message to display (e.g., "Loading studies...")
 * @param {string} size - The size variant: 'inline', 'page', or 'fullscreen'
 * @param {string} variant - The visual variant: 'spinner' or 'skeleton'
 * @param {number} timeout - Optional timeout in milliseconds
 * @param {function} onTimeout - Optional callback when timeout is reached
 */
const Loading = ({
  message = 'Loading...',
  size = 'page',
  variant = 'spinner',
  timeout = null,
  onTimeout = null
}) => {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (timeout && timeout > 0) {
      const timer = setTimeout(() => {
        setTimedOut(true);
        if (onTimeout) {
          onTimeout();
        }
      }, timeout);

      return () => clearTimeout(timer);
    }
  }, [timeout, onTimeout]);

  // Inline loading - small spinner for inline use
  if (size === 'inline') {
    if (variant === 'spinner') {
      return (
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm text-gray-600">{message}</span>
        </div>
      );
    }
    // Skeleton for inline (simple pulse)
    return (
      <div className="flex items-center space-x-2">
        <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  // Page loading - centered in content area
  if (size === 'page') {
    if (variant === 'spinner') {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-medium text-gray-700 mb-2">{message}</p>
            {timedOut && (
              <p className="text-sm text-yellow-600 mt-2">
                This is taking longer than expected...
              </p>
            )}
          </div>
        </div>
      );
    }
    // Skeleton for page content
    return (
      <div className="py-12 px-4">
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="h-8 bg-gray-200 rounded animate-pulse w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3"></div>
          <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2"></div>
          <div className="h-32 bg-gray-200 rounded animate-pulse mt-6"></div>
        </div>
      </div>
    );
  }

  // Fullscreen loading - covers entire viewport
  if (size === 'fullscreen') {
    if (variant === 'spinner') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">{message}</h2>
            <p className="text-gray-500">Please wait...</p>
            {timedOut && (
              <p className="text-sm text-yellow-600 mt-3">
                This is taking longer than expected. Please check your connection.
              </p>
            )}
          </div>
        </div>
      );
    }
    // Skeleton for fullscreen
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="h-12 bg-gray-200 rounded animate-pulse w-1/4"></div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  // Default fallback
  return (
    <div className="flex items-center justify-center py-8">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-700">{message}</p>
      </div>
    </div>
  );
};

Loading.propTypes = {
  message: PropTypes.string,
  size: PropTypes.oneOf(['inline', 'page', 'fullscreen']),
  variant: PropTypes.oneOf(['spinner', 'skeleton']),
  timeout: PropTypes.number,
  onTimeout: PropTypes.func,
};

export default Loading;
